import './ManageTeam.scss'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId, selectAuthenticatedUser } from '../../store/auth/authSlice'
import React, { useState } from 'react'
import { useGetUsersQuery } from '../../store/user/user'
import { User } from '../../models/User'
import { TableColumn } from 'react-data-table-component'
import FormattedDate from '../../components/FormattedDate'
import { ActionButton } from '../../components/DataTable/ActionButton'
import { Col, Container, Dropdown, Row } from 'react-bootstrap'
import { CustomDropdownItem } from '../../components/Dropdown/CustomDropdownItem'
import { EditUserModal } from './EditUserModal'

import { DeleteUserModal } from './DeleteUserModal'
import { ZocksDataTable } from '../../components/DataTable/ZocksDataTable'

import { DEFAULT_PAGE_SIZE } from '../../common/constants'
import { formatDate } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { MissingScopeAction } from '../../components/ScopedBlock/models'
import { ScopedBlock } from '../../components/ScopedBlock/ScopedBlock'
import { Roles } from '../../components/Roles/Roles'
import { isAdmin, isOwner } from '../../common/userUtils'

export const TeamMembersTab = () => {
  const { userId = null, modal = null } = useParams()
  const navigate = useNavigate()
  const accountId = useAppSelector(selectAccountId)
  const authenticatedUser = useAppSelector(selectAuthenticatedUser)
  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE)
  const [page, setPage] = useState(1)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(userId)
  const [showEditUserModal, setShowEditUserModal] = useState<boolean>(modal === 'edit')
  const [showDeleteUserModal, setShowDeleteUserModal] = useState<boolean>(false)
  const {
    data,
    isFetching,
    refetch: refetchUsers,
  } = useGetUsersQuery({ accountId: accountId, pageRequest: { page: page - 1, size: perPage }, identityTypes: ['idp'] }, { refetchOnMountOrArgChange: true })

  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPage(page)
    setPerPage(newPerPage)
  }

  const handleOnClickEditUser = (user: User) => {
    setSelectedUserId(user.id)
    setShowEditUserModal(true)
    navigate(`/account/manage-team/users/${user.id}/edit`, { replace: true })
  }

  const handleOnClickDeleteUser = (user: User) => {
    setSelectedUserId(user.id)
    setShowDeleteUserModal(true)
  }

  const handleEditUserModalHide = () => {
    setShowEditUserModal(false)
    setSelectedUserId(null)
    refetchUsers()
    navigate(`/account/manage-team/`, { replace: true })
  }

  const handleDeleteUserModalHide = () => {
    setShowDeleteUserModal(false)
    setSelectedUserId(null)
    refetchUsers()
  }

  const columns: TableColumn<User>[] = [
    {
      name: 'Name',
      selector: (row) => row.name,
      sortable: true,
      grow: 2,
      cell: (row) =>
        row.id === authenticatedUser?.id || isAdmin(authenticatedUser) || isOwner(authenticatedUser) ? (
          <Link className='zocks-text-primary' to={`/account/manage-team/users/${row.id}`}>
            {row.name}
          </Link>
        ) : (
          row.name
        ),
    },
    {
      name: 'Email',
      selector: (row) => row.email,
      sortable: true,
      grow: 2,
      width: '25%',
    },
    {
      name: 'Role',
      selector: (row) => JSON.stringify(row.roles.map((role) => role.id)),
      sortable: true,
      wrap: true,
      cell: (row) => <Roles userRoleIds={row.roles.map((role) => role.id)} />,
    },
    {
      name: 'Date added',
      selector: (row) => (row.createdAt ? formatDate(row.createdAt) : ''),
      sortable: true,
      cell: (row) => <FormattedDate date={row.createdAt ? formatDate(row.createdAt) : ''} />,
      hide: 992,
      grow: 2,
    },
    {
      name: 'status',
      selector: (row) => (row.status ? row.status : ''),
      sortable: true,
      conditionalCellStyles: [
        {
          when: (row) => row.status === 'active',
          style: {
            color: 'var(--zocks-success)',
          },
        },
      ],
    },
    {
      ignoreRowClick: true,
      right: true,
      button: true,
      allowOverflow: true,
      width: '3rem',
      cell: (row) => (
        <ActionButton>
          <ScopedBlock
            scope={{
              scope: 'user:update',
              superScope: ['user:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <Dropdown.Item as={CustomDropdownItem} onClick={() => handleOnClickEditUser(row)}>
              Edit
            </Dropdown.Item>
          </ScopedBlock>
          <ScopedBlock
            scope={{
              scope: 'user:delete',
              superScope: ['user:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <Dropdown.Item as={CustomDropdownItem} onClick={() => handleOnClickDeleteUser(row)}>
              Delete
            </Dropdown.Item>
          </ScopedBlock>
        </ActionButton>
      ),
    },
  ]

  return (
    <Container>
      {selectedUserId ? <EditUserModal userId={selectedUserId} show={showEditUserModal} onHide={handleEditUserModalHide} /> : null}
      {selectedUserId ? (
        <DeleteUserModal user={data?.data.find((user) => user.id === selectedUserId)} show={showDeleteUserModal} onHide={handleDeleteUserModalHide} />
      ) : null}
      <Row>
        <Col className='p-0'>
          <ZocksDataTable
            columns={columns}
            data={data?.data ?? []}
            progressPending={isFetching}
            pagination
            paginationServer
            paginationTotalRows={data?.page.totalElements}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
          />
        </Col>
      </Row>
    </Container>
  )
}
